import { h, mount } from "@/lib/dom";
import { icon } from "@/lib/icons";
import { openModal, toast } from "@/components/ui";
import { topicsService, pbErrorMessage } from "@/services";
import { pathService, IMPORTED_SOURCE } from "@/services/path.service";
import { syncStreak } from "@/services/streak.service";
import { parsePathImport } from "@/features/pfad/importPath";
import { pfRoot, pfHead, pfButton, pfEmpty, pfIconButton } from "@/features/pfad/shell";
import { seqColor, type Lesson, type Sequence } from "@/features/pfad/types";
import { navigate, type RouteContext } from "@/app/router";
import type { Topic } from "@/types/models";

/**
 * Der Lernpfad eines Themas: Sequenzen untereinander, in jeder Sequenz die
 * Lektionen als versetzte Knoten – wie bei Duolingo.
 *
 * Gesperrt ist eine Lektion, solange die davor nicht geschafft ist. Das ist
 * der ganze Reiz des Formats: es gibt immer genau einen nächsten Schritt.
 */
export async function PathPage(ctx: RouteContext): Promise<HTMLElement> {
  const topicId = ctx.params.id;
  const back = () => navigate(`/topic/${topicId}/learn`);

  let topic: Topic;
  try {
    topic = await topicsService.get(topicId);
  } catch (err) {
    return pfRoot(pfEmpty("Thema nicht gefunden", pbErrorMessage(err), pfButton("Zurück", back, "gray")));
  }

  void syncStreak();

  const head = pfHead(back);
  const stage = h("div", { class: "pf-stage" });
  const root = pfRoot(head.el, stage);

  async function render(): Promise<void> {
    let lessons: Lesson[];
    try {
      lessons = await pathService.listByTopic(topicId);
    } catch (err) {
      mount(stage, h("div", { class: "pf-body" }, pfEmpty("Pfad nicht ladbar", pbErrorMessage(err),
        pfButton("Nochmal versuchen", () => void render(), "blue"))));
      return;
    }

    if (!lessons.length) {
      mount(stage, h("div", { class: "pf-body" }, startCard()));
      return;
    }

    const sequences = pathService.toSequences(lessons);
    const doneCount = lessons.filter((l) => l.done).length;
    head.set(doneCount, 0, lessons.length);

    mount(
      stage,
      h(
        "div",
        { class: "pf-body" },
        h("div", { class: "pf-path" }, ...pathBody(sequences, lessons, doneCount))
      )
    );
  }

  /** Kopfbereich + alle Sequenzen. */
  function pathBody(sequences: Sequence[], lessons: Lesson[], doneCount: number): HTMLElement[] {
    const nextLesson = lessons.find((l) => !l.done) ?? null;
    const seqOfNext = nextLesson ? sequences.find((s) => s.lessons.includes(nextLesson)) : null;

    const hero = h(
      "div",
      { class: `pf-hero c-${seqColor(seqOfNext?.pos ?? 0)}` },
      h(
        "div",
        { style: "flex:1;min-width:0" },
        h("div", { class: "lvl" }, seqOfNext ? `Abschnitt ${(seqOfNext.pos ?? 0) + 1}` : "Geschafft"),
        h("h2", null, seqOfNext?.name ?? topic.title),
        h("div", { class: "sub" }, `${doneCount} von ${lessons.length} Lektionen · ${lessons.reduce((n, l) => n + l.xp, 0)} XP`)
      ),
      h("span", { class: "row", html: icon("target", 24), style: "color:#fff" })
    );

    const blocks: HTMLElement[] = [hero];
    let unlockedFound = false;

    for (const seq of sequences) {
      blocks.push(
        h("div", { class: `pf-seq-head c-${seqColor(seq.pos)}` }, h("span", null, seq.name))
      );
      seq.lessons.forEach((lesson, i) => {
        // Genau eine Lektion ist „dran": die erste nicht erledigte.
        const isNext = !lesson.done && !unlockedFound;
        if (isNext) unlockedFound = true;
        const locked = !lesson.done && !isNext;
        blocks.push(nodeRow(lesson, seq, i, { isNext, locked }));
      });
    }

    blocks.push(
      h(
        "div",
        { style: "margin-top:28px;display:flex;gap:10px;flex-wrap:wrap;justify-content:center" },
        exportStructureBtn("gray"),
        pfButton("Aufgaben importieren", () => openDetailImport(), "gray"),
        pfButton("Zurück zum Thema", back, "ghost")
      )
    );
    return blocks;
  }

  /** Ein Knoten mit Versatz nach links/rechts und Popover beim Antippen. */
  function nodeRow(
    lesson: Lesson,
    seq: Sequence,
    indexInSeq: number,
    state: { isNext: boolean; locked: boolean }
  ): HTMLElement {
    // Schlangenlinie: 0, +52, +72, +52, 0, -52, -72, -52, …
    const wave = [0, 52, 72, 52, 0, -52, -72, -52];
    const dx = wave[(indexInSeq + seq.pos) % wave.length];

    const node = h(
      "button",
      {
        class: "pf-node" + (state.locked ? " locked" : "") + (lesson.done ? " done" : ""),
        type: "button",
        title: lesson.title,
        onclick: () => (state.locked ? toast("Erst die Lektion davor abschließen.") : togglePop()),
      },
      h("span", { class: "row", html: icon(lesson.done ? "check" : lesson.icon, 34) })
    );

    const pop = h(
      "div",
      { class: `pf-pop c-${seqColor(seq.pos)}` },
      h("div", { class: "step" }, state.isNext ? "Jetzt dran" : lesson.done ? "Geschafft" : `Lektion ${indexInSeq + 1}`),
      h("div", { class: "name" }, lesson.title),
      h("div", { class: "goal" }, lesson.goal || `AFB ${lesson.afb}`),
      startLesson(lesson)
    );

    const row = h(
      "div",
      { class: "pf-node-row" },
      h(
        "div",
        {
          class: "pf-node-pos" + (state.isNext ? " pf-bob" : ""),
          style: `transform: translateX(${dx}px)`,
        },
        state.isNext ? h("div", { class: `pf-halo c-${seqColor(seq.pos)}` }) : null,
        state.isNext ? h("div", { class: `pf-bubble c-${seqColor(seq.pos)}` }, "START") : null,
        h("div", { class: "pf-ground" }),
        node,
        pop
      )
    );
    row.classList.add(`c-${seqColor(seq.pos)}`);

    let open = false;
    function togglePop(): void {
      open = !open;
      // Immer nur ein Popover offen – zwei nebeneinander überdecken sich.
      stage.querySelectorAll(".pf-pop.show").forEach((el) => {
        if (el !== pop) {
          el.classList.remove("show");
          el.closest(".pf-node-row")?.classList.remove("open");
        }
      });
      pop.classList.toggle("show", open);
      row.classList.toggle("open", open);
    }

    return row;
  }

  function startLesson(lesson: Lesson): HTMLElement {
    if (!lesson.questions.length && lesson.source_hash === IMPORTED_SOURCE) {
      const b = pfButton("Aufgaben für diesen Abschnitt holen", () => openSectionExport(lesson.sequence), "gold");
      b.classList.add("block");
      return b;
    }
    const b = pfButton(lesson.done ? "Wiederholen" : "Starten", () => {
      b.disabled = true;
      b.textContent = lesson.questions.length ? "Startet …" : "Aufgaben werden erzeugt …";
      navigate(`/topic/${topicId}/pfad/${lesson.id}`);
    });
    b.classList.add("block");
    return b;
  }

  /* ---------------- Pfad über eine externe KI (Export/Import, 2 Schritte) ---------------- */

  function startCard(): HTMLElement {
    const card = pfEmpty(
      "Noch kein Lernpfad",
      "Schritt 1: Gliederung exportieren – ein kurzer Auftrag, den eine KI (ChatGPT, Gemini, Claude, Perplexity …) in Sekunden beantwortet. " +
        "Danach entscheidest du, welchen Abschnitt die KI im Detail mit Aufgaben füllt (Schritt 2 unten im Pfad).",
      exportStructureBtn("green")
    );
    card.append(
      h("div", { style: "margin-top:10px;display:flex;justify-content:center" }, pfButton("Gliederung importieren", () => openStructureImport(), "gray"))
    );
    return card;
  }

  function exportStructureBtn(variant: "green" | "gray"): HTMLElement {
    const b = pfIconButton("Schritt 1: Gliederung exportieren", "file", () => {
      b.disabled = true;
      const label = b.lastChild as Text;
      void (async () => {
        try {
          const existing = await pathService.listByTopic(topicId).catch(() => [] as Lesson[]);
          const { exportPromptPdf } = await import("@/features/learn/promptPdf");
          await exportPromptPdf(topic, (m) => (label.textContent = m), {
            kind: "structure",
            existingLessons: existing.map((l) => l.title),
          });
          toast("PDF erstellt – bei einer KI hochladen, Antwort hier importieren.", "success");
        } catch (err) {
          toast(err instanceof Error ? err.message : pbErrorMessage(err), "error");
        } finally {
          label.textContent = "Schritt 1: Gliederung exportieren";
          b.disabled = false;
        }
      })();
    }, variant);
    return b;
  }

  /** Schritt 2: für einen Abschnitt (Sequenz) der KI nur dessen Lektionstitel mitgeben, Aufgaben zurückholen. */
  function openSectionExport(sequenceName: string): void {
    const status = h("p", { class: "muted text-sm" });
    const run = async () => {
      try {
        const lessons = await pathService.listByTopic(topicId);
        const lessonTitles = lessons.filter((l) => l.sequence === sequenceName).map((l) => l.title);
        status.textContent = "Erstellt PDF …";
        const { exportPromptPdf } = await import("@/features/learn/promptPdf");
        await exportPromptPdf(topic, (m) => (status.textContent = m), { kind: "section", sectionName: sequenceName, lessonTitles });
        handle.close();
        toast("PDF erstellt – bei einer KI hochladen, Antwort danach hier über „Importieren“ einfügen.", "success");
      } catch (err) {
        status.textContent = err instanceof Error ? err.message : pbErrorMessage(err);
      }
    };
    const handle = openModal({
      title: `Abschnitt „${sequenceName}“ füllen`,
      body: h(
        "div",
        null,
        h("p", null, `Auftrag für genau diesen Abschnitt exportieren, bei einer KI abschicken, Antwort danach über „Importieren“ einfügen.`),
        status
      ),
      footer: h(
        "div",
        { class: "row", style: "gap:8px;justify-content:flex-end" },
        h("button", { class: "btn btn-primary", type: "button", onclick: () => void run() }, "PDF exportieren")
      ),
    });
  }

  function openStructureImport(): void {
    openImportDialog({
      title: "Gliederung importieren",
      hint: "Antwort aus Schritt 1 einfügen – legt Abschnitte und Lektionen OHNE Aufgaben an. Aufgaben holst du danach je Abschnitt über den Knopf in der jeweils ersten Lektion.",
      allowEmpty: true,
      showReplace: true,
    });
  }

  function openDetailImport(): void {
    openImportDialog({
      title: "Aufgaben importieren",
      hint: "Antwort aus Schritt 2 (ein Abschnitt mit Aufgaben) einfügen. Lektionen mit gleichem Titel werden ergänzt, dein Fortschritt bleibt erhalten.",
      allowEmpty: false,
      showReplace: false,
    });
  }

  function openImportDialog(opts: { title: string; hint: string; allowEmpty: boolean; showReplace: boolean }): void {
    const input = h("textarea", {
      class: "input",
      rows: 12,
      placeholder: "Komplette Antwort der KI hier einfügen (der JSON-Block reicht) …",
      style: "width:100%;font-family:ui-monospace,monospace;font-size:12px",
    }) as HTMLTextAreaElement;
    const replace = h("input", { type: "checkbox" }) as HTMLInputElement;
    const info = h("p", { class: "muted text-sm" });
    const run = async () => {
      let parsed: ReturnType<typeof parsePathImport>;
      try {
        parsed = parsePathImport(input.value, opts.allowEmpty);
      } catch (err) {
        info.textContent = (err as Error).message;
        return;
      }
      try {
        const r = await pathService.importLessons(topicId, parsed.sections, opts.showReplace && replace.checked);
        handle.close();
        const parts = [
          `${r.added} neue Lektion${r.added === 1 ? "" : "en"}`,
          r.updated ? `${r.updated} aktualisiert (Fortschritt bleibt)` : "",
          r.removed ? `${r.removed} entfernt` : "",
          parsed.dropped ? `${parsed.dropped} ungültige Aufgabe${parsed.dropped === 1 ? "" : "n"} übersprungen` : "",
        ].filter(Boolean);
        toast(parts.join(" · "), "success");
        await render();
      } catch (err) {
        info.textContent = pbErrorMessage(err);
      }
    };
    const handle = openModal({
      title: opts.title,
      wide: true,
      body: h(
        "div",
        null,
        input,
        h("p", { class: "muted text-sm", style: "margin-top:6px" }, opts.hint),
        opts.showReplace
          ? h(
              "label",
              { class: "row", style: "gap:8px;margin-top:10px;align-items:center" },
              replace,
              "Bisherigen Pfad ersetzen (Lektionen mit gleichem Titel bleiben erledigt)"
            )
          : null,
        info
      ),
      footer: h(
        "div",
        { class: "row", style: "gap:8px;justify-content:flex-end" },
        h("button", { class: "btn btn-primary", type: "button", onclick: () => void run() }, "Importieren")
      ),
    });
    requestAnimationFrame(() => input.focus());
  }

  await render();
  return root;
}
